import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext, YearContext } from "../../context/context";
import { useLanguage } from "../../context/LanguageContext";
import { useToast } from "../../context/ToastContext";
import Card from "./Card";
import { TbHeart, TbEye, TbCirclePlus } from "react-icons/tb";
import { BsPatchCheckFill } from "react-icons/bs";
import { addWish } from "../../api";
import AddWishForm from "../Forms/AddWishForm";
import Popup from "../Elements/Popup";
import Toast from "../Elements/Toast";

const CardWishlistMobile = ({ className, showViewInWishlistButton = false, isInTab = false, customTitle, customDescription }) => {
  const { userData, setUserData } = useContext(AuthContext);
  const { selectedYear } = useContext(YearContext);
  const { copy } = useLanguage();
  const { showToast } = useToast();
  
  const [wishlistData, setWishlistData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [popupType, setPopupType] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const formRef = useRef(null);
  const submitHandlerRef = useRef(null);

  // Popup management
  const openPopup = (type) => setPopupType(type);
  const closePopup = () => setPopupType(null);

  // Handles submission of a new wish
  const handleSubmitWish = async (formData) => {
    try {
      const wishData = new FormData();
      wishData.append("wish_title", formData.wish_title);
      wishData.append("wish_img", formData.wish_img[0]);
      wishData.append("wish_url", formData.wish_url || "");
      wishData.append("wish_price", formData.wish_price);
      wishData.append("wish_currency", formData.wish_currency);
      wishData.append("year", selectedYear);
      wishData.append("user_id", userData.id);

      const response = await addWish(wishData, userData.id);

      if (!response || !response.success) {
        console.error("Failed to add wish:", response);
        setToast({ message: copy.wishlist.add.error, type: "error" });
        return;
      }

      const newWish = response.wish;

      // Update user data with the new wish
      setUserData((prevUserData) => ({
        ...prevUserData,
        wishlists: [newWish, ...(prevUserData.wishlists || [])],
      }));

      setToast({ message: copy.wishlist.add.success, type: "success" });
      closePopup();
    } catch (err) {
      console.error("Error submitting wish:", err);
      setToast({
        message: err.message || copy.wishlist.add.error_generic,
        type: "error",
      });
    }
  };

  // Filter wishes for the selected year
  useEffect(() => {
    if (!selectedYear || !userData?.wishlists) {
      setWishlistData([]);
      return;
    }

    try {
      setLoading(true);
      const filteredWishes = userData.wishlists.filter((wish) => wish.year === selectedYear);
      
      // Sort wishes by status: incomplete first, then completed
      const sortedWishes = filteredWishes.sort((a, b) => {
        if (a.status === true && b.status !== true) return 1;
        if (a.status !== true && b.status === true) return -1;
        return 0;
      });
      
      setWishlistData(sortedWishes);
    } catch (error) {
      console.error('Error filtering wishlist data:', error);
      showToast(`Error loading wishlist data: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [selectedYear, userData, showToast]);

  const cardTitle = customTitle || (isInTab 
    ? "Wishlist" 
    : "Wishlist");
  
  const cardDescription = customDescription || (isInTab 
    ? "Your wishes and dreams for this year" 
    : "Your wishes and dreams for this year");

  const wishlistDropdown = [
    {
      text: copy?.dropdown?.add_wish || "Add wish",
      onClick: () => openPopup("add-wish"),
      icon: <TbCirclePlus />,
    },
    {
      text: copy?.dropdown?.view_all || "View all",
      link: "/wishlist",
      icon: <TbEye />,
    },
  ];

  if (loading) {
    return (
      <Card
        title={cardTitle}
        description={cardDescription}
        className={`${className || ""}`}
      >
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Card>
    );
  }

  if (wishlistData.length === 0) {
    return (
      <>
        <Card
          title={cardTitle}
          description={cardDescription}
          options={wishlistDropdown}
          className={`${className || ""}`}
        >
          <div className="flex flex-col items-center justify-center h-32">
            <TbHeart size={32} className="mb-2" />
            <p className="text-sm text-center">No wishes for this year</p>
          </div>
        </Card>

        <Popup
          isOpen={popupType === "add-wish"}
          onClose={closePopup}
          title={copy.card_wishlist.popup.add_title}
          buttonSecondaryCTA={copy.dropdown.cancel}
          onClickSecondaryLink={closePopup}
          buttonPrimaryCTA={copy.dropdown.add}
          onClickPrimaryLink={() => {
            if (submitHandlerRef.current) {
              submitHandlerRef.current(); // Trigger form submission from parent
            }
          }}
        >
          <AddWishForm
            userData={userData}
            selectedYear={selectedYear}
            handleSubmitWish={handleSubmitWish}
            formRef={formRef}
            submitHandlerRef={submitHandlerRef}
          />
        </Popup>
      </>
    );
  }

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      <Card
        title={cardTitle}
        description={cardDescription}
        options={wishlistDropdown}
        className={`${className || ""}`}
        carousel={true}
      >
        {wishlistData.map((wish, index) => (
          <div
            key={wish.id}
            className="flex-shrink-0 w-[120px] relative"
          >
              <div className="relative w-full h-[120px] card-base-border rounded-lg overflow-hidden">
                <img
                  src={wish.img}
                  alt={wish.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Status indicator */}
                {wish.status && (
                  <div className="absolute top-2 right-2">
                    <BsPatchCheckFill
                      size={20}
                      className="text-accent-dark bg-white p-0.5 rounded-full drop-shadow-md"
                    />
                  </div>
                )}
              </div>
          </div>
        ))}
      </Card>

      <Popup
        isOpen={popupType === "add-wish"}
        onClose={closePopup}
        title={copy.card_wishlist.popup.add_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.add}
        onClickPrimaryLink={() => {
          if (submitHandlerRef.current) {
            submitHandlerRef.current(); // Trigger form submission from parent
          }
        }}
      >
        <AddWishForm
          userData={userData}
          selectedYear={selectedYear}
          handleSubmitWish={handleSubmitWish}
          formRef={formRef}
          submitHandlerRef={submitHandlerRef}
        />
      </Popup>
    </>
  );
};

export default CardWishlistMobile;
